// HOW ROUTEZERO WORKS + WHAT YOU GET — one compact section built around a 45 s explainer video
// (public/media/routezero-how-it-works.mp4 + .webm, ~3–4 MB each; made from real RouteZero screens; source project and
// storyboard: design/video/README.md). It replaces the two pinned scroll tracks.
//
// Playback rules:
//   - muted + playsinline, native controls (play/pause, sound, fullscreen), poster image
//   - autoplays (muted) only while at least half of it is on screen, and pauses when it leaves;
//     once the viewer pauses it themselves it never autoplays again on this page
//   - prefers-reduced-motion: no autoplay at all — the poster shows until the viewer presses play
// The same content is on the page as text (three steps + six benefits), so nothing depends on the video.
import { h } from './h.js';
import { HOW_STEPS } from './photo-marquee.js';

export const VIDEO_SRC = 'media/routezero-how-it-works.mp4'; // H.264/AAC: Chrome, Safari, Edge, Firefox
export const VIDEO_WEBM = 'media/routezero-how-it-works.webm'; // VP9/Opus fallback for browsers without H.264
export const POSTER_SRC = 'media/routezero-how-it-works-poster.jpg';
export const BENEFITS = ['Start from zero', 'Learn by doing', 'Solve realistic problems', 'Follow a structured learning path', 'Verify your fixes', 'Track your progress'];
// The single-file build inlines these as data: URIs (scripts/build-single.mjs).
const inlined = (p) => (typeof globalThis !== 'undefined' && globalThis.__NELL_IMAGES__?.[p]) || p;
const REDUCED = '(prefers-reduced-motion: reduce)';

export function explainerVideo() {
  const video = h('video', {
    class: 'rz-xv-video', 'data-testid': 'how-video', poster: inlined(POSTER_SRC),
    muted: true, playsinline: true, preload: 'metadata', controls: true, width: 1920, height: 1080,
    'aria-label': 'How RouteZero works: a 45-second video of real RouteZero screens, no narration',
    'aria-describedby': 'rz-how-summary',
  }, h('source', { src: inlined(VIDEO_SRC), type: 'video/mp4' }), h('source', { src: inlined(VIDEO_WEBM), type: 'video/webm' }));
  video.muted = true; video.defaultMuted = true; video.playsInline = true; // attributes alone are not enough on some browsers

  const section = h('section', { class: 'rz-section rz-xv', id: 'rz-how', 'aria-labelledby': 'rz-how-title', 'data-testid': 'landing-video', 'data-cleanup': '' },
    h('div', { class: 'rz-xv-copy' },
      h('span', { class: 'rz-eyebrow' }, 'HOW IT WORKS · WHAT YOU GET'),
      h('h2', { id: 'rz-how-title' }, 'How ', h('span', { class: 'rz-grad' }, 'RouteZero'), ' Works'),
      h('div', { id: 'rz-how-summary' },
        h('ol', { class: 'rz-xv-steps', 'data-testid': 'how-steps' }, HOW_STEPS.map((s) => h('li', {}, h('b', {}, s.n), h('span', {}, s.cap)))),
        h('p', { class: 'rz-xv-get' }, 'What you get with RouteZero:'),
        h('ul', { class: 'rz-xv-benefits', 'data-testid': 'how-benefits' }, BENEFITS.map((b) => h('li', {}, b))))),
    h('div', { class: 'rz-xv-frame' }, video));

  let io = null; let userPaused = false; let auto = false; const mq = window.matchMedia?.(REDUCED);
  const tryPlay = () => { if (userPaused || mq?.matches) return; auto = true; const p = video.play(); if (p?.catch) p.catch(() => {}); };
  const autoPause = () => { if (!video.paused) { auto = true; video.pause(); } };
  video.addEventListener('pause', () => { if (!auto && !video.ended) userPaused = true; auto = false; });
  video.addEventListener('play', () => { auto = false; });
  requestAnimationFrame(() => {
    if (!section.isConnected || !('IntersectionObserver' in window)) return;
    io = new IntersectionObserver(([e]) => { if (e.isIntersecting && e.intersectionRatio >= 0.5) tryPlay(); else autoPause(); }, { threshold: [0, 0.5] });
    io.observe(video);
  });
  section.__video = video;
  section.__cleanup = () => { io?.disconnect(); video.pause(); section.dataset.cleaned = 'true'; };
  return section;
}
